import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';
import { useStudentProfile } from '@/contexts/StudentProfileContext';

function useIsReady() {
  const auth = useAuth();
  const hasLocalIdentity = typeof document !== 'undefined' && document.cookie.includes('X-Local-Identity=');
  return auth.isAuthenticated || hasLocalIdentity;
}

import {
  createInquiry,
  fetchMyInquiries,
  fetchThread,
  fetchMessages,
  sendMessage,
  markThreadAsRead,
  closeThread,
  fetchTeacherInquiries,
  fetchTeacherThread,
  sendTeacherMessage,
  markTeacherThreadAsRead,
  closeTeacherThread,
  type CreateInquiryRequest,
} from '@/services/inquiryService';

// --- Student hooks ---

export function useCreateInquiry() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  // The coin balance is debited server-side the moment the inquiry is SENT, so
  // the UI has to re-read it right away. invalidateQueries cannot do that: the
  // balance lives in StudentProfileContext's useState, not in a TanStack Query
  // cache, so refreshProfile(true) is the only thing that actually refetches it.
  // StudentProfileProvider wraps every route in App.tsx, so this hook is always
  // called inside the provider.
  const { refreshProfile } = useStudentProfile();

  return useMutation({
    mutationFn: async (data: CreateInquiryRequest) => {
      const token = await auth.getToken();
      return createInquiry(token, data);
    },
    // Success path only — a 402/409 rejection moved no coins, so there is
    // nothing to re-read on failure.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myInquiries'] });
      refreshProfile(true);
    },
  });
}

export function useMyInquiries(page = 0, size = 20, status?: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['myInquiries', page, size, status],
    queryFn: () => fetchMyInquiries(token, page, size, status),
    enabled: isReady,
    staleTime: 5 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function useThread(threadId: string | undefined) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['inquiryThread', threadId],
    queryFn: () => fetchThread(token, threadId!),
    enabled: isReady && !!threadId,
  });
}

export function useMessages(threadId: string | undefined, page = 0, size = 50) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['inquiryMessages', threadId, page, size],
    queryFn: () => fetchMessages(token, threadId!, page, size),
    enabled: isReady && !!threadId,
    staleTime: 3 * 1000,
    refetchInterval: 5 * 1000,
  });
}

export function useSendMessage(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const token = await auth.getToken();
      return sendMessage(token, threadId, content);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', threadId] });
      queryClient.invalidateQueries({ queryKey: ['inquiryThread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['myInquiries'] });
      // A student's reply-back can flip this teacher's review eligibility
      // server-side (ADR-126 valid-exchange signal) — refetch regardless of
      // which teacher's thread this is, since threadId alone doesn't carry
      // teacherUserId here.
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'myReviewFor' });
    },
  });
}

export function useMarkAsRead(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await auth.getToken();
      return markThreadAsRead(token, threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myInquiries'] });
      queryClient.invalidateQueries({ queryKey: ['inquiryThread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', threadId] });
    },
  });
}

export function useCloseThread(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await auth.getToken();
      return closeThread(token, threadId);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['inquiryThread', threadId], data);
      queryClient.invalidateQueries({ queryKey: ['myInquiries'] });
    },
  });
}

// --- Teacher hooks ---

export function useTeacherInquiries(page = 0, size = 20, status?: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['teacherInquiries', page, size, status],
    queryFn: () => fetchTeacherInquiries(token, page, size, status),
    enabled: isReady,
    staleTime: 5 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function useTeacherThread(threadId: string | undefined) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['teacherThread', threadId],
    queryFn: () => fetchTeacherThread(token, threadId!),
    enabled: isReady && !!threadId,
  });
}

export function useSendTeacherMessage(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const token = await auth.getToken();
      return sendTeacherMessage(token, threadId, content);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', threadId] });
      queryClient.invalidateQueries({ queryKey: ['teacherThread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['teacherInquiries'] });
    },
  });
}

export function useMarkTeacherAsRead(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await auth.getToken();
      return markTeacherThreadAsRead(token, threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherInquiries'] });
      queryClient.invalidateQueries({ queryKey: ['teacherThread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', threadId] });
    },
  });
}

export function useCloseTeacherThread(threadId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await auth.getToken();
      return closeTeacherThread(token, threadId);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['teacherThread', threadId], data);
      queryClient.invalidateQueries({ queryKey: ['teacherInquiries'] });
    },
  });
}
